import { Fragment, type ReactNode } from 'react';
import './quality.css';

/**
 * Read-only view of a rated answer (the feedback snapshot). buddy3's chat ReportView draws every row type; triage only
 * needs to read what the user saw, so this keeps text, tables and charts (as their numbers) and shows any other row
 * as collapsed JSON. Nothing is rendered as HTML.
 */

type Obj = Record<string, unknown>;
const isObj = (v: unknown): v is Obj => !!v && typeof v === 'object' && !Array.isArray(v);

export type AnswerRow =
  | { type: 'text'; text: string }
  | { type: 'table'; title?: string; columns: string[]; data: Obj[]; truncated?: boolean }
  | { type: 'other'; kind: string; content: unknown };

export interface Answer {
  heading?: string;
  /** Markdown. */
  text: string;
  rows: AnswerRow[];
  note?: string;
}

/** Old Buddy nested a row's payload under its type (`content.table`). */
const unwrap = (type: string, c: unknown) =>
  isObj(c) && isObj(c[type]) && Object.keys(c).length <= 2 ? c[type] : c;

function tableRow(c: unknown): AnswerRow {
  if (!isObj(c)) return { type: 'table', columns: [], data: [] };
  const title = typeof c.title === 'string' ? c.title : typeof c.name === 'string' ? c.name : undefined;
  // `{headers, rows: [[…]]}` (old Buddy) or `{columns, data: [{…}]}`; columns may be `{title, dataIndex}` objects.
  if (Array.isArray(c.headers) && Array.isArray(c.rows)) {
    const columns = c.headers.map(String);
    const data = c.rows.map((r) =>
      Array.isArray(r) ? Object.fromEntries(r.map((v, i) => [columns[i] ?? String(i), v])) : isObj(r) ? r : {}
    );
    return { type: 'table', title, columns, data };
  }
  const data = Array.isArray(c.data) ? c.data.filter(isObj) : [];
  const columns = Array.isArray(c.columns)
    ? c.columns
        .map((col) => (isObj(col) ? (col.dataIndex ?? col.key ?? col.title) : col))
        .filter((x) => x != null && x !== '')
        .map(String)
    : Object.keys(data[0] ?? {});
  return { type: 'table', title, columns, data, truncated: c.truncated === true };
}

/** A chart's numbers as a table: one row per category, one column per series. */
function chartRow(c: unknown): AnswerRow | null {
  if (!isObj(c)) return null;
  const labels = c.xaxis ?? c.labels ?? c.categories;
  const series = Array.isArray(c.series) ? c.series : Array.isArray(c.datasets) ? c.datasets : null;
  if (!Array.isArray(labels) || !series) return null;
  const named = series.filter(isObj).map((s, i) => ({
    name: String(s.name ?? s.label ?? `Series ${i + 1}`),
    values: Array.isArray(s.data) ? s.data : Array.isArray(s.values) ? s.values : [],
  }));
  const first = typeof c.x === 'string' ? c.x : 'Category';
  return {
    type: 'table',
    title: typeof c.title === 'string' ? c.title : undefined,
    columns: [first, ...named.map((s) => s.name)],
    data: labels.map((l, i) => ({
      [first]: l,
      ...Object.fromEntries(named.map((s) => [s.name, s.values[i]])),
    })),
  };
}

/** Any stored answer shape → heading, markdown, rows (cf. buddy3 `normalizeReport`). */
export function toAnswer(raw: unknown): Answer {
  if (typeof raw === 'string') {
    const t = raw.trim();
    if (/^[`{]/.test(t)) {
      try {
        return toAnswer(JSON.parse(t.replace(/^```(?:json)?\s*/i, '').replace(/\s*```$/, '')));
      } catch {
        /* prose */
      }
    }
    return { text: raw, rows: [] };
  }
  if (!isObj(raw)) return { text: '', rows: [] };
  const src =
    isObj(raw.data) && !('answer' in raw) && !('summary' in raw) && !('rows' in raw) ? raw.data : raw;
  const text =
    typeof src.answer === 'string'
      ? src.answer
      : Array.isArray(src.summary)
        ? src.summary.filter((s) => typeof s === 'string').join('\n\n')
        : typeof src.summary === 'string'
          ? src.summary
          : typeof src.text === 'string'
            ? src.text
            : '';
  const rows: AnswerRow[] = [];
  for (const r of Array.isArray(src.rows) ? src.rows : []) {
    if (!isObj(r) || typeof r.type !== 'string') continue;
    const content = unwrap(r.type, r.content);
    if (r.type === 'insights' || r.type === 'message') {
      const t =
        typeof content === 'string'
          ? content
          : isObj(content)
            ? (content.text ?? content.markdown)
            : undefined;
      if (typeof t === 'string') rows.push({ type: 'text', text: t });
    } else if (r.type === 'table') rows.push(tableRow(content));
    else if (r.type === 'chart') rows.push(chartRow(content) ?? { type: 'other', kind: 'chart', content });
    else rows.push({ type: 'other', kind: r.type, content });
  }
  return {
    heading: typeof src.heading === 'string' && src.heading ? src.heading : undefined,
    text,
    rows,
    note: typeof src.note === 'string' && src.note ? src.note : undefined,
  };
}

/** `**bold**`, `*em*` and `` `code` `` inside a line. */
function inline(s: string): ReactNode[] {
  return s
    .split(/(\*\*[^*]+\*\*|`[^`]+`|\*[^*\s][^*]*\*)/g)
    .map((p, i) =>
      p.startsWith('**') && p.endsWith('**') && p.length > 4 ? (
        <b key={i}>{p.slice(2, -2)}</b>
      ) : p.startsWith('`') && p.endsWith('`') && p.length > 2 ? (
        <code key={i}>{p.slice(1, -1)}</code>
      ) : p.startsWith('*') && p.endsWith('*') && p.length > 2 ? (
        <em key={i}>{p.slice(1, -1)}</em>
      ) : (
        <Fragment key={i}>{p}</Fragment>
      )
    );
}

/** Enough markdown for an answer: headings, lists, fenced code, paragraphs; tables stay as text. */
export function Markdown({ text }: { text: string }) {
  const out: ReactNode[] = [];
  const lines = text.replace(/\r\n/g, '\n').split('\n');
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (!line.trim()) {
      i++;
    } else if (line.trimStart().startsWith('```')) {
      const body: string[] = [];
      for (i++; i < lines.length && !lines[i].trimStart().startsWith('```'); i++) body.push(lines[i]);
      i++;
      out.push(<pre key={out.length}>{body.join('\n')}</pre>);
    } else if (/^#{1,6}\s/.test(line)) {
      out.push(<h4 key={out.length}>{inline(line.replace(/^#+\s*/, ''))}</h4>);
      i++;
    } else if (/^\s*([-*+]|\d+[.)])\s/.test(line)) {
      const ordered = /^\s*\d/.test(line);
      const items: string[] = [];
      for (; i < lines.length && /^\s*([-*+]|\d+[.)])\s/.test(lines[i]); i++)
        items.push(lines[i].replace(/^\s*([-*+]|\d+[.)])\s+/, ''));
      const li = items.map((t, k) => <li key={k}>{inline(t)}</li>);
      out.push(ordered ? <ol key={out.length}>{li}</ol> : <ul key={out.length}>{li}</ul>);
    } else if (line.trimStart().startsWith('|')) {
      const body: string[] = [];
      for (; i < lines.length && lines[i].trimStart().startsWith('|'); i++) body.push(lines[i]);
      out.push(<pre key={out.length}>{body.join('\n')}</pre>);
    } else {
      const para: string[] = [];
      for (
        ;
        i < lines.length &&
        lines[i].trim() &&
        !/^(#{1,6}\s|\s*```|\s*([-*+]|\d+[.)])\s|\s*\|)/.test(lines[i]);
        i++
      )
        para.push(lines[i]);
      out.push(<p key={out.length}>{inline(para.join(' '))}</p>);
    }
  }
  return <div className="bd-md">{out}</div>;
}

const cell = (v: unknown) => (v == null ? '' : typeof v === 'object' ? JSON.stringify(v) : String(v));
const MAX_ROWS = 50;

function SmallTable({ row }: { row: Extract<AnswerRow, { type: 'table' }> }) {
  const shown = row.data.slice(0, MAX_ROWS);
  return (
    <figure className="bd-answer-table">
      {row.title && <figcaption>{row.title}</figcaption>}
      <div className="nyk-scroll">
        <table className="nyk-ui-table">
          <thead>
            <tr>
              {row.columns.map((c) => (
                <th key={c}>{c}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {shown.map((r, i) => (
              <tr key={i}>
                {row.columns.map((c) => (
                  <td key={c}>{cell(r[c])}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {(row.data.length > MAX_ROWS || row.truncated) && (
        <p className="nyk-ui-hint">
          {row.data.length > MAX_ROWS
            ? `First ${MAX_ROWS} of ${row.data.length} rows.`
            : 'The answer showed a truncated table.'}
        </p>
      )}
    </figure>
  );
}

export function AnswerView({ raw }: { raw: unknown }) {
  const a = toAnswer(raw);
  if (!a.heading && !a.text && !a.rows.length && !a.note)
    return <p className="nyk-ui-hint">The answer was not stored.</p>;
  return (
    <div className="bd-answer" data-testid="answer">
      {a.heading && <h3>{a.heading}</h3>}
      {a.text && <Markdown text={a.text} />}
      {a.rows.map((r, i) =>
        r.type === 'text' ? (
          <Markdown key={i} text={r.text} />
        ) : r.type === 'table' ? (
          <SmallTable key={i} row={r} />
        ) : (
          <details key={i} className="bd-answer-other">
            <summary>{r.kind}</summary>
            <pre className="oph-json">{JSON.stringify(r.content, null, 2)}</pre>
          </details>
        )
      )}
      {a.note && <p className="nyk-ui-hint">{a.note}</p>}
    </div>
  );
}
